// src/stores/GeneralInfoStore.ts
import { create } from 'zustand';
import { sharedState } from '../config/config';

// ─── Cookie helpers ───────────────────────────────────────────────────────────
function getCookie(name: string): string {
    const match = document.cookie
        .split('; ')
        .find(row => row.startsWith(name + '='));
    if (!match) return '';
    return decodeURIComponent(match.split('=')[1]);
}

function setCookie(
    name: string,
    value: string,
    days = 7,
    path = '/'
): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    document.cookie = [
        `${name}=${encodeURIComponent(value)}`,
        `expires=${expires.toUTCString()}`,
        `path=${path}`,
    ].join('; ');
}

// ─── Read initial values ──────────────────────────────────────────────────────
const initialCategory = getCookie('category');
const initialActiveTab = getCookie('activeTab');

// ─── Store definition ─────────────────────────────────────────────────────────
type GeneralInfoStore = {
    category: string;
    setCategory: (newCategory: string) => void;

    currentPage: string;
    setCurrentPage: (newPage: string) => void;

    isCookieBannerVisible: boolean;
    setIsCookieBannerVisible: (visible: boolean) => void;

    sharedVariable: boolean;
    toggleSharedVariable: () => void;

    activeTab: string;
    setActiveTab: (newTab: string) => void;
};

export const useGeneralInfoStore = create<GeneralInfoStore>((set) => ({
    // ─ persisted in cookies ────────────────────────────────────────────────────
    category: initialCategory,
    setCategory: (newCategory) => {
        setCookie('category', newCategory, 30);
        set({ category: newCategory });
    },

    activeTab: initialActiveTab,
    setActiveTab: (newTab) => {
        setCookie('activeTab', newTab, 30);
        set({ activeTab: newTab });
    },

    // ─ in-memory only ─────────────────────────────────────────────────────────
    currentPage: '',
    setCurrentPage: (newPage) => set({ currentPage: newPage }),

    isCookieBannerVisible: false,
    setIsCookieBannerVisible: (visible) =>
        set({ isCookieBannerVisible: visible }),

    sharedVariable: sharedState ? true : false,
    toggleSharedVariable: () =>
        set((state) => ({ sharedVariable: !state.sharedVariable })),
}));
